import React from 'react';
import { Link } from 'react-router-dom';
import PublicShell from '../../components/PublicShell/PublicShell';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import nisaMeterImg from '../../assets/landing/NissaMeter.jpeg';
import nisaControlImg from '../../assets/landing/NissaControl.png';
import './LandingInfo.css';

/**
 * Página de información.
 *
 * Comparte marco, tipografía, color y tema con el acceso y con la aplicación:
 * el recorrido información -> acceso -> panel ya no atraviesa tres productos
 * distintos.
 *
 * Lo que había antes y ya no está, y por qué:
 *   - Pantalla de carga de 800 ms fingida. No cargaba nada.
 *   - Paralaje atado a `window.addEventListener('scroll')`, que recalcula en
 *     cada fotograma y da tirones en móvil.
 *   - Contadores animados sobre cifras que son estimaciones, no medidas.
 *   - Iconos de Font Awesome traídos de un CDN cuando el proyecto ya usa una
 *     familia de iconos propia.
 */

const principles = [
  {
    title: 'Misión',
    body:
      'Revolucionar la gestión del consumo de agua mediante tecnología inteligente que optimiza recursos y potencia la eficiencia energética, transformando la forma en que la sociedad interactúa con el agua para construir un futuro sostenible.',
  },
  {
    title: 'Visión',
    body:
      'Ser la empresa líder en México en soluciones inteligentes para el ahorro de agua, reconocida por su compromiso con la innovación, la sustentabilidad y el bienestar social.',
  },
  {
    title: 'Valores',
    body:
      'Responsabilidad social, innovación tecnológica, compromiso ambiental, calidad y mejora continua como principios que guían la creación de soluciones modernas y accesibles.',
  },
];

const deviceFeatures = [
  'Medición en tiempo real',
  'Detección de fugas',
  'Conectividad IoT',
  'Disponible en 3 modelos: 1/2", 1" y 2" pulgadas',
];

const appFeatures = [
  'Panel de control intuitivo',
  'Alertas personalizadas',
  'Reportes detallados',
];

export default function LandingInfo() {
  return (
    <PublicShell>
      {/* ================= Hero ================= */}
      <section id="home" className="lp-hero">
        <div className="lp-hero__text">
          <h1 className="lp-hero__title">
            Tecnología inteligente para una cultura del{' '}
            <span className="lp-hero__accent">cuidado del agua</span>
          </h1>
          <p className="lp-hero__body">
            Revolucionamos la gestión del consumo de agua con soluciones
            innovadoras que optimizan recursos y potencian la eficiencia
            energética.
          </p>
          <div className="lp-hero__actions">
            <a href="#products" className="ng-btn ng-btn--primary ng-btn--lg">
              Conoce nuestras soluciones
            </a>
            <Link to="/login" className="ng-btn ng-btn--secondary ng-btn--lg">
              Acceder a la plataforma
            </Link>
          </div>
        </div>

        <figure className="lp-hero__figure">
          <img
            src={nisaMeterImg}
            alt="NisaMeter instalado sobre la tubería principal, con electroválvula de corte"
            className="lp-hero__photo"
          />
        </figure>
      </section>

      {/* ================= Sobre NisaGuard ================= */}
      <section id="about" className="lp-section">
        <h2 className="lp-section__title">Sobre NisaGuard</h2>

        <div className="lp-principles">
          {principles.map(({ title, body }) => (
            <article className="lp-principles__item" key={title}>
              <h3 className="lp-principles__title">{title}</h3>
              <p className="lp-principles__body">{body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ================= Soluciones ================= */}
      <section id="products" className="lp-section">
        <h2 className="lp-section__title">NisaMeter</h2>
        <p className="lp-section__lead">
          Un medidor conectado a la toma principal y una aplicación para leerlo
          y controlarlo desde cualquier sitio.
        </p>

        <article className="lp-product">
          <figure className="lp-product__figure">
            <img
              src={nisaMeterImg}
              alt="Medidor NisaMeter y electroválvula montados en línea sobre la tubería"
              loading="lazy"
            />
          </figure>
          <div className="lp-product__text">
            <h3 className="lp-product__kind">El dispositivo</h3>
            <p>
              Dispositivo inteligente de medición que monitorea en tiempo real
              el consumo de agua, proporcionando datos precisos para optimizar
              el uso del recurso.
            </p>
            <ul className="lp-features">
              {deviceFeatures.map((feature) => (
                <li key={feature}>
                  <CheckRoundedIcon />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </article>

        <article className="lp-product lp-product--reverse">
          <figure className="lp-product__figure">
            <img
              src={nisaControlImg}
              alt="Panel de NisaMeter mostrando consumo, presión y estado de los dispositivos"
              loading="lazy"
            />
          </figure>
          <div className="lp-product__text">
            <h3 className="lp-product__kind">La aplicación</h3>
            <p>
              Aplicación intuitiva que permite gestionar y controlar el consumo
              de agua desde cualquier dispositivo, con análisis detallados y
              recomendaciones personalizadas.
            </p>
            <ul className="lp-features">
              {appFeatures.map((feature) => (
                <li key={feature}>
                  <CheckRoundedIcon />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </article>
      </section>

      {/* ================= Estimaciones =================
          Son estimaciones y se dicen como tales. Antes subían con un contador
          animado, que es exactamente el gesto que hace parecer medido lo que
          no lo está. */}
      <section className="lp-section">
        <div className="lp-figures">
          <div className="lp-figures__item">
            <span className="lp-figures__value">10%</span>
            <span className="lp-figures__label">
              Ahorro de agua diario estimado
            </span>
          </div>
          <div className="lp-figures__item">
            <span className="lp-figures__value">600 L</span>
            <span className="lp-figures__label">
              Litros ahorrados al día, estimados
            </span>
          </div>
          <p className="lp-figures__note">
            Cifras estimadas a partir del consumo medio de una vivienda. El
            ahorro real depende de la instalación y de los hábitos de uso.
          </p>
        </div>
      </section>

      {/* ================= Cierre ================= */}
      <section id="app" className="lp-cta">
        <div>
          <h2 className="lp-cta__title">Comienza a ahorrar agua hoy</h2>
          <p className="lp-cta__body">
            Únete a la revolución del consumo inteligente de agua con NisaGuard.
          </p>
        </div>
        {/* Mismo texto que el resto de accesos de la página: una intención,
            una etiqueta. */}
        <Link to="/login" className="ng-btn ng-btn--primary ng-btn--lg">
          Acceder a la plataforma
          <ArrowForwardRoundedIcon />
        </Link>
      </section>
    </PublicShell>
  );
}
